// @flow
import React, { type Node, useCallback } from 'react'

import {
  dateFormat,
  journalPathMask,
  getStudentIdExact,
  getClassId,
  getDate,
  getIsSummary,
  params as router,
  createPath,
} from 'app/pages/routePaths'

import Journal from 'app/components/Apps/Dnevnik/Journal'
import StudentsSelector from 'app/components/StudentsSelector'
import SimpleDate from 'app/dataProviders/SimpleDate'

import DnevnikRoot from './DnevnikRoot'
import Nav from './Nav'

type Props = {
  params: Object,
  router: {
    replace: (string) => void,
    push: (string) => void,
  },
}

const JournalPage = ({ params, router: { replace, push } }: Props): Node => {
  const studentID = getStudentIdExact(params)
  const classID = getClassId(params)
  const date = getDate(params)
  const summary = getIsSummary(params)

  const handleRedirect = useCallback(
    (exactParams: { +date?: string, +klassId: string }, writeHistory?: boolean = false) => {
      const redirectTo = createPath(
        {
          [router.studentID]: studentID,
          [router.classID]: exactParams.klassId,
          [router.date]: exactParams.date,
          [router.summary]: summary,
        },
        journalPathMask,
      )

      if (writeHistory) {
        push(redirectTo)
        return
      }
      replace(redirectTo)
    },
    [studentID, replace, push, summary],
  )

  const handleCreatePath = useCallback(
    ({ date: dt, summary: sm }: { date?: SimpleDate, summary: boolean }) => {
      return createPath(
        {
          [router.studentID]: studentID,
          [router.classID]: classID,
          [router.summary]: sm ? router.summary : '',
          ...(dt && dt.getIsValid() ? { [router.date]: dt.getString(dateFormat) } : {}),
        },
        journalPathMask,
      )
    },
    [studentID, classID],
  )

  return (
    <DnevnikRoot studentId={studentID}>
      <Nav studentId={studentID} params={params} />
      <Journal
        studentId={studentID}
        klassId={classID}
        date={date}
        dateFormat={dateFormat}
        showSummary={!!summary}
        additionalControls={<StudentsSelector />}
        redirect={handleRedirect}
        createPath={handleCreatePath}
      />
    </DnevnikRoot>
  )
}

export default JournalPage
